import React, {useMemo} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkGemoji from 'remark-gemoji';
import remarkBreaks from 'remark-breaks';
import {parseTimestamps} from '@/modules/music_sniper/components/comments/comment_section';
import {UserPopover} from '@/modules/music_sniper/components/comments/user_popover';

type TimestampLinkProps = {
    timestamp: string;
    clickedTimestamp: (timestamp: string) => void;
};

const TimestampLink = (props: TimestampLinkProps) => {
    return (
        <a
            style={{
                display: 'inline-block',
                padding: '0.1em 0.2em',
                marginBottom: '0.2em',
                backgroundColor: 'var(--mantine-color-default-border)',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                fontSize: 'var(--mantine-font-size-sm)',
                color: 'var(--mantine-color-text)',
            }}
            href='#'
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                props.clickedTimestamp(props.timestamp);
            }}
        >
            {props.timestamp}
        </a>
    );
};

type MentionLinkProps = {
    userId: string;
    username: string;
    workspaceId: string;
};

const MentionLink = (props: MentionLinkProps) => {
    return (
        <UserPopover userId={props.userId} userName={props.username}>
            <span
                style={{
                    display: 'inline-block',
                    padding: '0.1em 0.3em',
                    backgroundColor: 'var(--mantine-color-default-border)',
                    borderRadius: '4px',
                    fontSize: 'var(--mantine-font-size-sm)',
                    color: 'var(--mantine-color-text)',
                    fontWeight: 500,
                    cursor: 'pointer',
                }}
            >
                @{props.username}
            </span>
        </UserPopover>
    );
};

type WorkspaceUser = {
    id: string;
    name?: string | null;
    email?: string | null;
};

type TimestampAwareMarkdownProps = {
    text: string;
    clickedTimestamp: (timestamp: string) => void;
    className?: string;
    workspaceUsers?: WorkspaceUser[];
    workspaceId?: string;
};

const parseMentions = (text: string, workspaceUsers: WorkspaceUser[]): string => {
    const mentionRegex = /@([a-zA-Z0-9\s.]+?)(?=\s|$|[,!?;:])/g;

    return text.replace(mentionRegex, (match, username) => {
        const trimmedUsername = username.trim();

        const matchedUser = workspaceUsers.find(user =>
            user.name?.toLowerCase() === trimmedUsername.toLowerCase() ||
            user.email?.toLowerCase() === trimmedUsername.toLowerCase()
        );

        if (matchedUser) {
            return `[@${trimmedUsername}](#mention-${matchedUser.id})`;
        }

        return match;
    });
};

export const TimestampAwareMarkdown = (props: TimestampAwareMarkdownProps) => {
    return useMemo(() => {
        let markdownText = props.text.split(/(\s+)/).map(part => {
            if (part.trim() === '') {
                return part;
            }

            const timestamps = parseTimestamps(part);
            if (!timestamps.length) {
                return part;
            }

            const timestamp = timestamps[0];
            return `[${timestamp}](${timestamp})`;
        }).join('');

        if (props.workspaceUsers && props.workspaceUsers.length > 0) {
            markdownText = parseMentions(markdownText, props.workspaceUsers);
        }

        return (
            <Markdown
                remarkPlugins={[remarkGfm, remarkGemoji, remarkBreaks]}
                className={props.className}
                components={{
                    a: ({children, node, ...markdownProps}) => {
                        const linkText = String(children).toString();
                        const href = markdownProps.href || '';

                        if (href.startsWith('#mention-')) {
                            const userId = href.replace('#mention-', '');
                            const username = linkText.replace('@', '');
                            return (
                                <MentionLink
                                    userId={userId}
                                    username={username}
                                    workspaceId={props.workspaceId || ''}
                                />
                            );
                        }

                        const timestamps = linkText ? parseTimestamps(linkText) : [];
                        if (!timestamps.length || timestamps[0] !== linkText) {
                            return (
                                <a
                                    {...markdownProps}
                                    target='_blank'
                                >
                                    {children}
                                </a>
                            );
                        }

                        return (
                            <TimestampLink
                                clickedTimestamp={props.clickedTimestamp}
                                timestamp={timestamps[0]}
                            />
                        );
                    },
                }}
            >
                {markdownText}
            </Markdown>
        );
    }, [props.text, props.clickedTimestamp, props.className, props.workspaceUsers]);
};
